import { Button, Divider, Flex, Group, Loader, Modal, Stack, Text } from '@mantine/core';
import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import type { IProdoqCompanyResponse, ProdoqPermissionKey } from '../../../../../shared/types/prodoq';
import { prodoqPermissionStore } from '../../../../../shared/store/prodoqPermission.store';
import PermissionGrantTable from './ui/PermissionGrantTable';
import PermissionGuide from './ui/PermissionGuide';
import PermissionMenuTable from './ui/PermissionMenuTable';
import {
  createPermissionRequestPayload,
  createPermissionRows,
  findSelectedMenu,
  getDefaultSelectedMenuId,
} from '../utils/permissionMenu';
import useGetPermissions from '../hooks/useGetPermissions';
import usePutPermission from '../hooks/usePutPermission';

interface PermissionModalProps {
  opened: boolean;
  onClose: () => void;
  selectedCompany: IProdoqCompanyResponse | null;
}

export default function PermissionModal(props: PermissionModalProps) {
  const { opened, onClose, selectedCompany } = props;
  const { currentCompanyId, menus, roles, selectedMenuId, setSelectedMenuId, updateMenuPermission } = prodoqPermissionStore(
    useShallow((state) => ({
      currentCompanyId: state.companyId,
      menus: state.menus,
      roles: state.roles,
      selectedMenuId: state.selectedMenuId,
      setSelectedMenuId: state.setSelectedMenuId,
      updateMenuPermission: state.updateMenuPermission,
    })),
  );

  useGetPermissions({
    companyId: selectedCompany?.companyId ?? 0,
    enabled: opened && !!selectedCompany?.companyId,
  });

  const { mutate: putPermission } = usePutPermission();

  const selectedMenu = useMemo(() => findSelectedMenu(menus, selectedMenuId), [menus, selectedMenuId]);

  const permissions = useMemo(() => {
    if (!selectedMenu) {
      return [];
    }

    return createPermissionRows(roles, selectedMenu.permissions);
  }, [roles, selectedMenu]);

  const isPermissionLoading = opened && !!selectedCompany?.companyId && currentCompanyId !== selectedCompany.companyId;

  const handleChangePermission = (roleId: number, permissionKey: ProdoqPermissionKey, checked: boolean) => {
    if (!selectedMenuId) {
      return;
    }

    updateMenuPermission(selectedMenuId, roleId, permissionKey, checked);
  };

  const handleClickSave = () => {
    const permissionPayload = createPermissionRequestPayload(menus);
    putPermission({
      companyId: selectedCompany?.companyId ?? 0,
      data: permissionPayload,
    });
  };

  useEffect(() => {
    if (!menus.length) {
      setSelectedMenuId(null);
      return;
    }

    if (selectedMenuId && findSelectedMenu(menus, selectedMenuId)) {
      return;
    }

    setSelectedMenuId(getDefaultSelectedMenuId(menus));
  }, [menus, selectedMenuId, setSelectedMenuId]);

  return (
    <Modal opened={opened} onClose={onClose} title="권한 관리" centered size={1000}>
      <Group mb="md">
        <Text fw={500} size="sm">
          회사
        </Text>
        <Text size="sm">{selectedCompany?.name ?? '-'}</Text>
      </Group>

      {isPermissionLoading ? (
        <Flex justify="center" align="center" mih={320}>
          <Loader color="secondary" />
        </Flex>
      ) : !menus.length ? (
        <Flex justify="center" align="center" mih={320}>
          <Text size="sm" c="dimmed">
            표시할 권한 메뉴가 없습니다.
          </Text>
        </Flex>
      ) : (
        <Flex align="stretch">
          <PermissionMenuTable menus={menus} selectedMenuId={selectedMenuId} onSelectMenuId={setSelectedMenuId} />
          <Divider orientation="vertical" mx="lg" />

          <Stack flex={1}>
            <Text fw={500} size="sm">
              권한 부여
              <Text component="span" c="secondary" pl={8}>
                *
              </Text>
            </Text>
            <PermissionGrantTable
              permissions={permissions}
              selectedMenuId={selectedMenuId}
              onChangePermission={handleChangePermission}
            />
            <PermissionGuide />
          </Stack>
        </Flex>
      )}
      <Group justify="center" mt="md">
        <Button variant="outline" onClick={onClose}>
          취소
        </Button>
        <Button color="secondary" onClick={handleClickSave}>
          저장
        </Button>
      </Group>
    </Modal>
  );
}
